import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Alert,
  Box,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Menu,
  MenuItem,
  DialogActions,
  DialogContentText,
  FormControl,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { PAGE_SIZE_OPTIONS, PAGE_SIZES } from '@/constants/pagination';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StyledButton from '@/components/common/styledButton';
import CreateAccountGroupForm from './CreateAccountGroupForm';
import { useState, useMemo } from 'react';
import {
  useGetAccountTagsQuery,
  AccountGroupCategory,
  useDeleteAccountGroupMutation,
} from '@/store/accountsApi';
import EditAccountGroupForm from './EditAccountGroupForm';
import SetAccountGroupDefaults from './SetAccountGroupDefaults';
import AssignAccountSegmentsModal from './AssignAccountSegmentsModal';
import { StyledTabs, StyledTab } from '@/components/common/styledTabs';
import { useLocalization } from '@/hooks/useLocalization';
import { mobileWidth } from '@/constants';
import { actionColumnConfig } from '@/components/grids/gridColumnConfigs';

interface ManageAccountGroupsModalProps {
  open: boolean;
  onClose: () => void;
  showDescriptionColumn?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`tabpanel-${String(index)}`}
      aria-labelledby={`tab-${String(index)}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const ManageAccountGroupsModal = ({
  open,
  onClose,
  showDescriptionColumn = false,
}: ManageAccountGroupsModalProps) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileWidth = useMediaQuery(mobileWidth);
  const [mode, setMode] = useState<'list' | 'add' | 'edit' | 'setDefaults'>(
    'list',
  );
  const [editingGroup, setEditingGroup] = useState<AccountGroupCategory | null>(
    null,
  );
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGroup, setSelectedGroup] =
    useState<AccountGroupCategory | null>(null);
  const [showAssignSegmentsModal, setShowAssignSegmentsModal] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const {
    data: accountGroups,
    isLoading,
    isError,
  } = useGetAccountTagsQuery({});
  const [deleteAccountGroup, { isLoading: isDeleting }] =
    useDeleteAccountGroupMutation();

  const advisorGroups = useMemo(
    () => accountGroups?.filter((g) => g.IsAdvisorTag) ?? [],
    [accountGroups],
  );

  const userGroups = useMemo(
    () => accountGroups?.filter((g) => !g.IsAdvisorTag) ?? [],
    [accountGroups],
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    group: AccountGroupCategory,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedGroup(group);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedGroup(null);
  };

  const handleEditClick = () => {
    if (selectedGroup) {
      setEditingGroup(selectedGroup);
      setMode('edit');
      handleMenuClose();
    }
  };

  const handleAssignSegments = () => {
    if (selectedGroup) {
      setEditingGroup(selectedGroup);
      setShowAssignSegmentsModal(true);
      handleMenuClose();
    }
  };

  const handleDeleteClick = () => {
    setAnchorEl(null);
    setConfirmDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setSelectedGroup(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedGroup) {
      try {
        await deleteAccountGroup(selectedGroup.TagCategoryId).unwrap();
      } catch (error) {
        console.error('Failed to delete account group', error);
      } finally {
        setConfirmDeleteOpen(false);
        setSelectedGroup(null);
      }
    }
  };

  const handleClose = () => {
    if (mode !== 'list') {
      setMode('list');
      setEditingGroup(null);
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    setMode('list');
    setEditingGroup(null);
  };

  const columns: GridColDef<AccountGroupCategory>[] = [
    {
      field: 'Name',
      headerName: t('manageAccountGroups.group'),
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 1.5 }}>
          <Typography variant='body1'>{params.row.Name}</Typography>
          {params.row.Description && (
            <Typography variant='caption'>{params.row.Description}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'Tags',
      headerName: t('manageAccountGroups.groupSegments'),
      flex: 1,
      valueGetter: (_value, row) => row.Tags.map((tag) => tag.Name).join(', '),
    },
    {
      field: 'actions',
      ...actionColumnConfig,
      width: 50,
      renderCell: (params) => (
        <IconButton
          size='small'
          onClick={(e) => {
            handleMenuClick(e, params.row);
          }}
          aria-label={t('moreOptions')}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    } as GridColDef<AccountGroupCategory>,
  ];

  const advisorGroupColumns: GridColDef<AccountGroupCategory>[] =
    showDescriptionColumn
      ? [
          {
            field: 'Name',
            headerName: t('manageAccountGroups.group'),
            flex: 1,
            renderCell: (params) => (
              <Typography
                variant='body1'
                sx={{ py: 1.5 }}
              >
                {params.row.Name}
              </Typography>
            ),
          },
          {
            field: 'Description',
            headerName: t(
              'manageAccountGroups.descriptionColumn',
              'Description',
            ),
            flex: 1,
            renderCell: (params) => (
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {params.row.Description ?? ''}
              </Typography>
            ),
          },
          columns[1],
        ]
      : columns.filter((c) => c.field !== 'actions');

  const getTitle = () => {
    switch (mode) {
      case 'add':
        return t('manageAccountGroups.createGroup');
      case 'edit':
        return t('manageAccountGroups.editGroup');
      case 'setDefaults':
        return t('manageAccountGroups.setDefaultsFor', {
          name: editingGroup?.Name,
        });
      default:
        return t('manageCustomAccountGroups');
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth='md'
        fullScreen={isMobile}
        sx={(theme) => ({
          [theme.breakpoints.down('sm')]: {
            '& .MuiPaper-root': {
              paddingTop:
                'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
            },
          },
        })}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='h5'
            sx={{ fontSize: '1.25rem' }}
          >
            {getTitle()}
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label={t('close')}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                my: 5,
                paddingBottom:
                  'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
              }}
            >
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Alert severity='error'>
              {t('manageAccountGroups.couldNotLoad')}
            </Alert>
          ) : mode === 'add' ? (
            <CreateAccountGroupForm onCancel={handleCancel} />
          ) : mode === 'edit' && editingGroup ? (
            <EditAccountGroupForm
              accountGroup={editingGroup}
              onCancel={handleCancel}
              onSetDefaultsClick={() => {
                setShowAssignSegmentsModal(true);
              }}
            />
          ) : mode === 'setDefaults' && editingGroup ? (
            <SetAccountGroupDefaults accountGroup={editingGroup} />
          ) : (
            <>
              <Typography
                variant='body1'
                color='text.primary'
                sx={{ mb: 3 }}
              >
                {t('manageAccountGroups.description')}
              </Typography>

              <Box>
                {isMobileWidth ? (
                  <FormControl
                    fullWidth
                    size='small'
                  >
                    <Select
                      labelId='account-groups-select-label'
                      id='account-groups-select'
                      value={tabValue.toString()}
                      onChange={(event: SelectChangeEvent) => {
                        setTabValue(parseInt(event.target.value, 10));
                      }}
                      sx={(theme) => ({
                        backgroundColor: theme.palette.custom.overlay.selected,
                        color: theme.palette.primary.main,
                        '& .MuiSelect-select': {
                          color: theme.palette.primary.main,
                          padding: '16px 14px',
                          fontWeight: 600,
                        },
                        '& .MuiSelect-icon': {
                          color: theme.palette.primary.main,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                          display: 'none',
                        },
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '& fieldset': {
                            border: 'none',
                          },
                          '&:hover fieldset': {
                            border: 'none',
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                          },
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                          display: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                          display: 'none',
                        },
                      })}
                    >
                      <MenuItem value='0'>
                        {t('manageAccountGroups.myCustomGroups')}
                      </MenuItem>
                      <MenuItem value='1'>
                        {t('manageAccountGroups.advisorCreatedGroups')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <StyledTabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label='account groups tabs'
                  >
                    <StyledTab
                      label={t('manageAccountGroups.myCustomGroups')}
                    />
                    <StyledTab
                      label={t('manageAccountGroups.advisorCreatedGroups')}
                    />
                  </StyledTabs>
                )}
              </Box>

              <TabPanel
                value={tabValue}
                index={0}
              >
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <StyledButton
                      size='large'
                      variant='contained'
                      onClick={() => {
                        setMode('add');
                      }}
                      sx={{
                        boxShadow: 'none',
                        marginTop: 2,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: '100%', sm: '200px' },
                      }}
                    >
                      {t('manageAccountGroups.addGroup')}
                    </StyledButton>
                  </Box>
                  {userGroups.length > 0 ? (
                    <DataGridPremium
                      rows={userGroups}
                      columns={columns}
                      getRowId={(row) => row.TagCategoryId}
                      autoHeight
                      pagination
                      hideFooter={userGroups.length <= 10}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: PAGE_SIZES.SMALL },
                        },
                      }}
                      pageSizeOptions={[...PAGE_SIZE_OPTIONS.MODAL]}
                      disableColumnMenu
                      disableRowSelectionOnClick
                      getRowHeight={() => 'auto'}
                      sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: theme.palette.grey[100],
                          borderBottom: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          py: 1,
                          display: 'flex',
                          alignItems: 'center',
                        },
                        '& .MuiDataGrid-row': {
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                          overflow: 'unset',
                        },
                      }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                      <Typography>
                        {t('manageAccountGroups.noCustomGroups')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>

              <TabPanel
                value={tabValue}
                index={1}
              >
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant='body1'
                    color='text.primary'
                    sx={{ mb: 3, pl: 2 }}
                  >
                    {t('manageAccountGroups.advisorGroupsDescription')}
                  </Typography>
                  {advisorGroups.length > 0 ? (
                    <DataGridPremium
                      rows={advisorGroups}
                      columns={advisorGroupColumns}
                      getRowId={(row) => row.TagCategoryId}
                      autoHeight
                      pagination
                      hideFooter={advisorGroups.length <= 10}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: PAGE_SIZES.SMALL },
                        },
                      }}
                      pageSizeOptions={[...PAGE_SIZE_OPTIONS.MODAL]}
                      disableColumnMenu
                      disableRowSelectionOnClick
                      getRowHeight={() => 'auto'}
                      sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: theme.palette.grey[100],
                          borderBottom: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          py: 1,
                          display: 'flex',
                          alignItems: 'center',
                        },
                        '& .MuiDataGrid-row': {
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                          overflow: 'unset',
                        },
                      }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                      <Typography>
                        {t('manageAccountGroups.noAdvisorGroups')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>
            </>
          )}
        </DialogContent>
        {mode === 'list' && (
          <DialogActions sx={{ justifyContent: 'flex-end' }}>
            <StyledButton
              size='large'
              variant='outlined'
              onClick={handleClose}
            >
              {t('close')}
            </StyledButton>
          </DialogActions>
        )}
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick}>{t('edit')}</MenuItem>
        <MenuItem onClick={handleAssignSegments}>
          {t('manageAccountGroups.assignSegments')}
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>{t('delete')}</MenuItem>
      </Menu>

      <Dialog
        open={confirmDeleteOpen}
        onClose={handleCancelDelete}
        aria-labelledby='confirm-delete-dialog-title'
      >
        <DialogTitle id='confirm-delete-dialog-title'>
          {t('manageAccountGroups.confirmDeleteTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('manageAccountGroups.confirmDeleteDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            onClick={handleCancelDelete}
          >
            {t('cancel')}
          </Button>
          <Button
            size='large'
            variant='outlined'
            onClick={() => {
              void handleConfirmDelete();
            }}
            color='error'
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {editingGroup && (
        <AssignAccountSegmentsModal
          open={showAssignSegmentsModal}
          onClose={() => {
            setShowAssignSegmentsModal(false);
          }}
          accountGroup={editingGroup}
        />
      )}
    </>
  );
};

export default ManageAccountGroupsModal;
